import React, { FC, ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.scss";
import classNames from "classnames";

interface TooltipProps {
  title?: ReactNode;
  text?: string;
  isVisible?: boolean;
  children?: ReactNode;
  className?: string;
  /** Sizes the anchor — e.g. a full-width input instead of inline content. */
  wrapperClassName?: string;
  type?: "tooltip" | "dropdown";
  compact?: boolean;
  placement?: "top" | "bottom" | "right" | "left";
  align?: "center" | "start";
  appendToBody?: boolean;
  boundaryRef?: React.RefObject<HTMLElement>;
  boundaryPadding?: number;
}

export const Tooltip: FC<TooltipProps> = ({
  title,
  text,
  isVisible: controlledIsVisible,
  children,
  className,
  wrapperClassName,
  type = "tooltip",
  compact = false,
  placement = "bottom",
  align = "center",
  appendToBody = false,
  boundaryRef,
  boundaryPadding = 8,
}) => {
  const [internalIsVisible, setInternalIsVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties | null>(
    null,
  );
  const hasValidDimensionsRef = useRef(false);

  const isVisible =
    controlledIsVisible !== undefined ? controlledIsVisible : internalIsVisible;

  const content = title || text;
  const shouldUsePortal = appendToBody && typeof document !== "undefined";

  useLayoutEffect(() => {
    if (!shouldUsePortal || !isVisible) {
      setPortalStyle(null);
      hasValidDimensionsRef.current = false;
      return;
    }

    const updatePosition = () => {
      const target = wrapperRef.current;
      const tooltipEl = tooltipRef.current;
      if (!target || !tooltipEl) return;

      const targetRect = target.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const spacing = 2;

      const viewportPadding = 35;
      let top: number;
      let left: number;

      const horizontalLeft =
        align === "start"
          ? targetRect.left
          : targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;

      if (placement === "top") {
        top = targetRect.top - tooltipRect.height - spacing;
        left = horizontalLeft;
      } else if (placement === "bottom") {
        top = targetRect.bottom + spacing;
        left = horizontalLeft;
      } else if (placement === "right") {
        top = targetRect.top + 35 - tooltipRect.height / 2;
        left = targetRect.right + spacing;
      } else if (placement === "left") {
        top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
        left = targetRect.left - tooltipRect.width - spacing;
      } else {
        top = targetRect.bottom + spacing;
        left = horizontalLeft;
      }

      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      let minLeft = viewportPadding;
      let maxLeft = viewportWidth - tooltipRect.width - viewportPadding;

      let minTop = viewportPadding;
      let maxTop = viewportHeight - tooltipRect.height - viewportPadding;

      if (boundaryRef?.current) {
        const boundaryRect = boundaryRef.current.getBoundingClientRect();
        minLeft = Math.max(minLeft, boundaryRect.left + boundaryPadding);
        maxLeft = Math.min(
          maxLeft,
          boundaryRect.right - tooltipRect.width - boundaryPadding,
        );
        minTop = Math.max(minTop, boundaryRect.top + boundaryPadding);
        maxTop = Math.min(
          maxTop,
          boundaryRect.bottom - tooltipRect.height - boundaryPadding,
        );
      }

      left = Math.min(Math.max(left, minLeft), Math.max(maxLeft, minLeft));

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        left = Math.min(left, maxLeft - scrollbarWidth);
      }

      if (
        placement === "top" ||
        placement === "left" ||
        placement === "right"
      ) {
        top = Math.max(Math.min(top, maxTop), minTop);
      } else {
        top = Math.min(Math.max(top, minTop), maxTop);
      }

      const isValidWidth = tooltipRect.width > 0 && tooltipRect.width < 1000;

      if (isValidWidth) {
        hasValidDimensionsRef.current = true;
      }

      const newPortalStyle = {
        position: "fixed" as const,
        top,
        left,
        right: "auto" as const,
        zIndex: 2000,
      };

      setPortalStyle(newPortalStyle);
    };

    const raf = requestAnimationFrame(updatePosition);
    const handleReposition = () => updatePosition();

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [shouldUsePortal, isVisible, placement, align, content]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setInternalIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setInternalIsVisible(false);
    }, 100);
  };

  // A press on the button the tooltip describes hides it. On a phone the
  // tap's emulated hover never leaves, so "Add new group" stayed on top of
  // the modal the button had just opened. Info icons (not buttons) keep
  // showing on a tap.
  const handleClick = (event: React.MouseEvent) => {
    if (!(event.target as Element).closest?.("button, a")) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setInternalIsVisible(false);
  };

  const tooltipElement =
    isVisible && content ? (
      <div
        ref={shouldUsePortal ? tooltipRef : null}
        className={classNames(
          styles.tooltipContent,
          {
            [styles.dropdown]: type === "dropdown",
            [styles.compact]: compact,
            [styles.top]: placement === "top" && !shouldUsePortal,
            [styles.bottom]: placement === "bottom" && !shouldUsePortal,
            [styles.right]: placement === "right" && !shouldUsePortal,
            [styles.left]: placement === "left" && !shouldUsePortal,
          },
          className,
        )}
        style={
          shouldUsePortal
            ? portalStyle && hasValidDimensionsRef.current
              ? {
                  ...portalStyle,
                  pointerEvents: "auto" as const,
                }
              : {
                  position: "fixed" as const,
                  top: 0,
                  left: 0,
                  right: "auto" as const,
                  visibility: "hidden" as const,
                  pointerEvents: "none" as const,
                  zIndex: 2000,
                }
            : undefined
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </div>
    ) : null;

  return (
    <div
      className={classNames(styles.tooltipWrapper, wrapperClassName)}
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}
      {tooltipElement &&
        (shouldUsePortal
          ? createPortal(tooltipElement, document.body)
          : tooltipElement)}
    </div>
  );
};
